'use client';

import { PublicPageLayout } from '@letsgo/PublicPageLayout';
import Link from 'next/link';
import { Box, Container, Typography, Link as MuiLink, Card, CardContent } from '@mui/material';
import { Lock, Shield } from '@mui/icons-material';
import { SECURITY_EMAIL } from '@/lib/site-contact';

const SecurityPage = () => {
  return (
    <PublicPageLayout>
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" fontWeight={700} mb={2} color="text.primary" component="h1">
          Security
        </Typography>
        <Typography variant="h6" color="text.secondary" mb={4}>
          How we protect your data and our systems.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="body1" color="text.primary">
            We take security seriously. Below is a high-level overview of our practices. For full details on data handling, see our Privacy Policy and Terms of Service.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Card variant="outlined" sx={{ flex: '1 1 260px', maxWidth: 360 }}>
              <CardContent>
                <Lock color="primary" sx={{ mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Encryption</Typography>
                <Typography variant="body2" color="text.secondary">
                  Data in transit is protected with TLS. Sensitive data at rest is encrypted. We follow industry standards for key and credential management.
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ flex: '1 1 260px', maxWidth: 360 }}>
              <CardContent>
                <Shield color="primary" sx={{ mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Auth &amp; access</Typography>
                <Typography variant="body2" color="text.secondary">
                  Secure sign-in (including OAuth where offered), role-based access, and session handling. We do not store plain-text passwords.
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Typography variant="h6" fontWeight={700} color="text.primary">
            Compliance &amp; data handling
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            We design our systems with privacy and security in mind and, where applicable, align with common frameworks and regulations. Data is used only as described in our Privacy Policy and Terms.
          </Typography>

          <Typography variant="h6" fontWeight={700} color="text.primary">
            Report a vulnerability
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            If you believe you have found a security vulnerability, please report it responsibly. Do not exploit the issue. Contact us at{' '}
            <MuiLink href={`mailto:${SECURITY_EMAIL}`} color="primary">
              {SECURITY_EMAIL}
            </MuiLink>
            {' '}with a clear description and steps to reproduce. We will acknowledge and work with you to resolve it.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <MuiLink component={Link} href="/privacy" color="primary" underline="hover">
              Privacy Policy
            </MuiLink>
            <MuiLink component={Link} href="/terms" color="primary" underline="hover">
              Terms of Service
            </MuiLink>
          </Box>
        </Box>
      </Container>
    </PublicPageLayout>
  );
};

export default SecurityPage;
